import React from "react";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import TextButton from "../ui/TextButton";
import { LazyMotion, domAnimation, m } from "framer-motion";
import Button, { ButtonType } from "../reusable/Button";
import ResponsiveImage from "../ui/ResponsiveImage";

interface ImageTextProps {
  data: ImageTextData;
}

export interface ImageTextData {
  sectionName?: string;
  title: string;
  text: string;
  buttonText: string;
  buttonLink: string;
  boxAligment: "left" | "right";
  image: string;
  image_link: string;
  tablet_image_link?: string;
  mobile_image_link?: string;
  alt?: string;
  background: "light" | "dark";
}

const imageTextVariants = cva(
  "flex flex-col justify-center px-6 md:px-12 py-12 md:py-16 min-h-[400px] desktop:min-h-[500px]",
  {
    variants: {
      background: {
        light: "bg-[#59697C]",
        dark: "bg-accent-color-three",
      },
    },
    defaultVariants: {
      background: "dark",
    },
  }
);

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function ImageText({ data }: ImageTextProps) {
  const isRight = data.boxAligment === "right";
  const sectionId = data.sectionName && data.sectionName.replace(/\s+/g, "-");

  const useCloudinary = data.image_link;
  const desktopImage = useCloudinary ? data.image_link : data.image;
  const tabletImage = data.tablet_image_link || desktopImage;
  const mobileImage =
    data.mobile_image_link || data.tablet_image_link || desktopImage;
  const altImage = data.alt || "Image Text Section";

  const titleClasses = cn(
    "font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop mb-6",
    data.background === "light" ? "text-white" : "text-accent-color-two"
  );

  const textSectionContent = (isDesktop: boolean) => (
    <div className="w-fit desktop:mx-auto">
      {isDesktop ? (
        <m.h2
          className={titleClasses}
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {data.title}
        </m.h2>
      ) : (
        <m.div
          className={titleClasses}
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          {data.title}
        </m.div>
      )}
      <m.p
        className={cn(
          data.background === "light" ? "text-white" : "text-[#FAFAFA]",
          "font-sans text-paragraph-large mb-8 max-w-md"
        )}
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        {data.text}
      </m.p>
      {data.buttonText && data.buttonLink && (
        <m.div
          variants={fadeIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="flex justify-start"
        >
          {data.background === "light" ? (
            <Button
              type={ButtonType.dark}
              title={data.buttonText.toUpperCase()}
              link={data.buttonLink}
              className="leading-6 tracking-[0.15em] text-[18px]"
            />
          ) : (
            <TextButton
              text={data.buttonText.toUpperCase()}
              link={data.buttonLink}
              textColor={"primary"}
            />
          )}
        </m.div>
      )}
    </div>
  );

  const textSection = (isDesktop: boolean = false) => (
    <div
      className={cn(
        imageTextVariants({ background: data.background || "dark" }),
        "w-full desktop:w-[40vw]",
        isRight
          ? "desktop:pl-[3.33vw] desktop:pr-[7.5vw]"
          : "desktop:pr-[3.33vw] desktop:pl-[7.5vw]"
      )}
    >
      {textSectionContent(isDesktop)}
    </div>
  );

  const imageSection = (
    <div className="relative w-full desktop:w-[60vw] min-h-[400px] desktop:min-h-[500px]">
      <ResponsiveImage
        desktop_image={desktopImage}
        tablet_image={tabletImage}
        mobile_image={mobileImage}
        alt={altImage}
        shouldFetch={!useCloudinary}
        className="object-cover"
      />
    </div>
  );

  return (
    <LazyMotion features={domAnimation}>
      <section
        id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
        data-name={data.sectionName}
        className="flex flex-col desktop:flex-row w-full min-h-[400px] desktop:min-h-[500px]"
      >
      <div className="flex flex-col desktop:hidden">
        {imageSection}
        {textSection(false)}
      </div>
      <div className="hidden desktop:flex desktop:flex-row w-full">
        {isRight ? (
          <>
            {imageSection}
            {textSection(true)}
          </>
        ) : (
          <>
            {textSection(true)}
            {imageSection}
          </>
        )}
      </div>
    </section>
    </LazyMotion>
  );
}
